<template lang="pug">
.send-message-approval-modal
  b-modal(ref="modal" size="md" no-close-on-backdrop no-close-on-esc @ok="submit")
    div(slot="modal-title")
      span 承認依頼

    .send-message-approval-modal-body
      .register-body.d-flex.flex-column
        //- 承認者選択行
        .register-row
          .register-group
            .register-key
              span.key-name 承認者
              span.validation-label 必須
            .register-value
              select.register-form.input-select(
                v-model="approverUserId"
                name="approver-user-id"
                v-validate="{ required: true }"
                data-vv-as="承認者"
                :class="{ noinput: !approverUserId }"
                style="width: 100%;"
              )
                option(value="" disabled) 承認者を選択してください
                option(v-for="account in accountOptions" :key="account.userId" :value="account.userId") {{ account.name }} （{{ account.userId }}）
            .error-messages(v-if="errors.has('approver-user-id')")
              div(v-for="error in errors.collect('approver-user-id')")
                label.error-message {{ error }}

        //- 承認者への補足・メッセージ入力行
        .register-row.mt-3
          .register-group
            .register-key
              span.key-name 承認者へのメッセージ
              span.comment ※任意
            .register-value
              textarea.register-form(
                v-model="comment"
                placeholder="承認者へのメッセージや補足（任意）"
                name="approval-comment"
                maxlength="500"
                rows="4"
                style="width: 100%;"
              )

    //- フッターボタン（依頼する / キャンセル）
    .send-message-approval-modal-footer(slot="modal-footer" slot-scope="{ ok, cancel }" style="width: 100%;")
      ValidationAlert(v-model="showValidationAlert")
      .buttons.d-flex.justify-content-end
        button.btn.modal-ok-btn(type="button" @click="ok()") 依頼する
        button.btn.modal-cancel-btn(type="button" @click="cancel()") キャンセル
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import StandardAccountsService, { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import { userStore } from "@/store/store";
import { Role } from "@/commons/services/Authorization";

/**
 * ### 承認依頼モーダルから親コンポーネントへ渡す送信データ構造
 */
export interface ApprovalModalSubmitData {
  /** 選択された承認者のユーザーID */
  approverId: string;
  /** 選択された承認者の氏名 */
  approverName: string;
  /** 承認者へのメッセージ・補足コメント */
  comment: string;
}

/**
 * ### メッセージ送信時の承認者選択および依頼コメント入力モーダルコンポーネント
 *
 * - 承認依頼ボタン押下時に表示され、施設内のユーザーアカウントから承認者を1人選択します。
 * - 任意で承認者への連絡コメントを入力して送信依頼を行うことができます。
 */
@Component({
  components: {
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class SendMessageApprovalModal extends Vue {
  /** 一般ユーザーアカウントサービス（承認者候補取得用） */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;

  /** バリデーションエラーアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 選択された承認者ユーザーID */
  approverUserId: string = "";
  /** 承認者への補足・依頼メッセージコメント */
  comment: string = "";

  /** 施設内に所属する承認者選択肢アカウント一覧 */
  accountOptions: Array<StandardAccountsGetModel> = [];

  /**
   * ### モーダル表示処理
   * - 入力フォーム（承認者選択、コメント）およびエラー表示を初期化
   * - 施設内のユーザーアカウント一覧を取得して選択肢に設定
   */
  public async show(): Promise<void> {
    this.approverUserId = "";
    this.comment = "";
    this.showValidationAlert = false;
    this.$validator.errors.clear();

    // 承認者選択肢のアカウント一覧を取得
    await this.loadAccounts();

    const modal: any = this.$refs.modal;
    if (modal) {
      modal.show();
    }
  }

  /**
   * ### 承認者選択肢として表示する施設アカウント一覧を取得する
   */
  private async loadAccounts(): Promise<void> {
    try {
      // ログイン中施設に属するアカウント一覧を取得
      const accounts = await this.standardAccountsService.get(userStore, { startIndex: 0, endIndex: 1000, all: "true" });
      // 有効なユーザーIDを持つアカウントかつ自分自身以外のユーザーをフィルター
      const currentUserId = userStore.loginUser ? userStore.loginUser.userId : "";
      this.accountOptions = accounts.filter((acc) => acc.userId && acc.userId != currentUserId && acc.role != Role.KM_USER && acc.deleteFlag == false);
    } catch (e) {
      this.accountOptions = [];
    }
  }

  /**
   * ### モーダル非表示処理
   */
  public hide(): void {
    const modal: any = this.$refs.modal;
    if (modal) {
      modal.hide();
    }
  }

  /**
   * ### 承認依頼実行（モーダルOKボタン押下時）処理
   * - 承認者の必須選択チェック等のバリデーションを実施
   * - 検証OKであれば選択された承認者情報とコメントを親コンポーネントへemitしてモーダルを閉じる
   *
   * @param modalEvent モーダルイベント
   */
  public async submit(modalEvent: any): Promise<void> {
    // デフォルトのダイアログ閉じ動作を防止（バリデーションOK時に手動で閉じるため）
    modalEvent.preventDefault();

    // VeeValidate による入力検証
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult === false) {
      // 入力エラーがある場合はアラートを表示
      this.showValidationAlert = true;
    } else {
      this.showValidationAlert = false;

      // 選択されたユーザーIDから承認者氏名を取得
      const selectedAccount = this.accountOptions.find((acc) => acc.userId === this.approverUserId);
      const approverName = selectedAccount ? selectedAccount.name || selectedAccount.userId : this.approverUserId;

      // 送信データ作成
      const resultData: ApprovalModalSubmitData = {
        approverId: this.approverUserId,
        approverName: approverName,
        comment: this.comment
      };

      // 親コンポーネントへイベント通知
      this.$emit("submit", resultData);
      this.hide();
    }
  }
}
</script>

<style lang="scss" scoped>
.send-message-approval-modal-body {
  padding: 15px;
}
.register-body {
  .register-row {
    margin-bottom: 0;
  }
}
</style>
